/* eslint-disable react/jsx-props-no-spreading */
/* eslint-disable @typescript-eslint/no-misused-promises */
import React, { useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  AppendableField,
  DatepickerField,
  InputField,
  NumericFormatField,
  RadioField,
  SelectField,
} from '../../Forms/Fields';
import {
  AdditionalSavings,
  BooleanOptions, GenderOptions, Genders,
  SavingsOption,
  SavingsTypeOptions,
  StringifiedBooleans,
} from '../../../utils/constants';
import { RadioBoxField } from '../../Forms/Fields/RadioBoxField';
import { ProjectsFormValues, projectsFormSchema, useProjectsForm } from './useProjectsForm';
import { ButtonLoading } from '../../common/ButtonLoading';

export function ProjectsForm() {
  const { handleSubmit, isLoading } = useProjectsForm();
  const methods = useForm<ProjectsFormValues>({
    defaultValues: {
      gender: GenderOptions.MALE,
      children: StringifiedBooleans.FALSE,
      wants_insurance_savings: StringifiedBooleans.TRUE,
      children_births: [],
      savings_option: SavingsOption.savings_capacity,
      monthly_savings: 0,
      goal_value: 0,
      net_salary: 0,
      has_other_investments: StringifiedBooleans.FALSE,
      other_investments: [],
    },
    resolver: zodResolver(projectsFormSchema),
  });

  const savingsOption = methods.watch('savings_option');
  const hasChildren = methods.watch('children') === StringifiedBooleans.TRUE;
  const hasOtherInvestments = methods.watch('has_other_investments') === StringifiedBooleans.TRUE;

  const handleTriggerErrors = () => methods.formState.errors;

  const onSubmit = (values: ProjectsFormValues) => {
    handleSubmit(values);
  };

  // Cleanup counterpart field on saving option radio box selection
  useEffect(() => {
    if (savingsOption === 'savings_capacity') {
      methods.setValue('goal_value', 0);
    }

    if (savingsOption === 'goal_value') {
      methods.setValue('monthly_savings', 0);
    }
  }, [savingsOption]);

  // Cleanup `children_births` field on `no` radio button selection
  useEffect(() => {
    if (!hasChildren) {
      methods.setValue('children_births', []);
    }
  }, [hasChildren]);

  // Cleanup `other_investments` on `no` radio button selection
  useEffect(() => {
    if (!hasOtherInvestments) {
      methods.setValue('other_investments', []);
    }
  }, [hasOtherInvestments]);

  return (
    <FormProvider {...methods}>
      <form className="card-form" onSubmit={methods.handleSubmit(onSubmit)}>
        <InputField<ProjectsFormValues>
          id="fullname"
          path="fullname"
          label="¿Cuál es tu nombre?"
          placeholder="Ingresa su nombre"
        />

        <DatepickerField<ProjectsFormValues>
          id="date_of_birth"
          path="date_of_birth"
          label="¿Cuál es tu fecha de nacimiento?"
        />

        <RadioField<ProjectsFormValues>
          path="gender"
          options={Genders.asRadioOptions({ omit: ['OTHER'] })}
          label="¿Cuál es tu género?"
        />

        <RadioField<ProjectsFormValues>
          path="children"
          options={BooleanOptions.asRadioOptions()}
          label="¿Tienes hijos?"
        />

        {hasChildren && (
          <div className="d-flex flex-column gap-4">
            <AppendableField
              hasDefaultField
              id="children_births"
              name="children_births"
              label="¿Cuál es la fecha de nacimiento de tu hijo/a?"
              defaultAppendButtonText="Agregar otro hijo"
              renderInput={(fieldId, index) => (
                <DatepickerField<ProjectsFormValues>
                  key={fieldId}
                  id={fieldId}
                  path={`children_births.${index}`}
                />
              )}
            />
          </div>
        )}

        <RadioField<ProjectsFormValues>
          path="wants_insurance_savings"
          options={BooleanOptions.asRadioOptions().reverse()}
          label="¿Quieres ahorrar con seguro?"
        />

        <RadioBoxField<ProjectsFormValues>
          path="savings_option"
          options={
            SavingsTypeOptions
              .asRadioBoxOptions({ select: ['savings_capacity', 'goal_value'] })
          }
          label="¿Cómo te gustaría ahorrar?"
        />

        {savingsOption === 'savings_capacity' && (
          <NumericFormatField<ProjectsFormValues>
            id="monthly_savings"
            label="¿Cuánto puedes ahorrar al mes?"
            path="monthly_savings"
            prefix="$ "
            placeholder="$"
          />
        )}

        {savingsOption === 'goal_value' && (
          <NumericFormatField<ProjectsFormValues>
            id="goal_value"
            label="¿Cuál es el valor de tu meta?"
            path="goal_value"
            prefix="$ "
            placeholder="$"
          />
        )}

        <NumericFormatField<ProjectsFormValues>
          id="net_salary"
          label="¿Cuál es tu sueldo líquido? (Opcional)"
          path="net_salary"
          prefix="$ "
          placeholder="$"
        />

        <RadioField<ProjectsFormValues>
          path="has_other_investments"
          options={BooleanOptions.asRadioOptions()}
          label="¿Tienes otros ahorros e inversiones? (Opcional)"
        />

        {hasOtherInvestments && (
          <AppendableField
            hasDefaultField
            id="other_investments"
            name="other_investments"
            renderInput={(fieldId, index) => (
              <SelectField<ProjectsFormValues>
                key={fieldId}
                id={fieldId}
                path={`other_investments.${index}`}
                options={AdditionalSavings.asSelectOptions()}
                placeholder="Selecciona"
                anchorBehavior="element"
              />
            )}
          />
        )}

        <ButtonLoading
          type="submit"
          isLoading={isLoading}
          onClick={handleTriggerErrors}
          className="btn btn-secondary py-3 mt-2"
          spinnerProps={{
            size: 24,
          }}
        >
          Recomendar Producto
        </ButtonLoading>
      </form>
    </FormProvider>
  );
}
